/**
 * NOC-OTA-COMPAT-GUARD — classificação de compatibilidade de release OTA app (UI).
 *
 * Espelha backend/app/services/ota_compat.py para que a UI mostre a MESMA decisão
 * que o backend vai aplicar (evita surpresa: o backend é a autoridade e recusa
 * 409/412, mas a UI gateia o botão e exige a frase antes de disparar).
 *
 * Puro / sem React / sem I/O → testável em node (frontend/test/ota_compat.test.mjs).
 * Motivação: incidente IPWD-D4EB9A88 (2.8.9→2.8.12 resetou NVS/config).
 */

// Devices em fw < 2.8.10 antecedem a estabilização do schema de config.
export const SCHEMA_STABLE_MIN = [2, 8, 10];
// Frase p/ risco LEVE de reset (cruza 2.8.10 sem ser release de migração).
export const RESET_CONFIRM_PHRASE = "ENTENDO QUE PODE RESETAR CONFIG";
// HARDEN-1 — override de BANCADA p/ salto PERIGOSO (só super_admin, backend audita).
export const DANGEROUS_OTA_ACK_PHRASE =
  "ENTENDO QUE ESTE OTA PODE RESETAR WIFI E DEIXAR O DEVICE OFFLINE";

const VER_RE = /(\d+)\.(\d+)\.(\d+)/;

export function parseVersion(v) {
  if (!v) return null;
  const m = VER_RE.exec(String(v));
  if (!m) return null;
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

function cmp(a, b) {
  for (let i = 0; i < 3; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}

function has(text, ...needles) {
  const t = (text || "").toLowerCase();
  return needles.some((n) => t.includes(n));
}

export function classifyRelease(version, notes = "", kind = "app") {
  const v = (version || "").toLowerCase();
  const n = (notes || "").toLowerCase();
  // Marcador de migração NO VERSION (sinal forte). Evita falso-positivo de factory
  // quando as NOTES só mencionam "full-flash FE-dev" como orientação.
  const version_migration = has(v, "plaintext-migration", "otafs2c-plaintext", "-migration");
  let factory = has(v, "-fe-lab", "-fe-dev");
  if (!factory && !version_migration) {
    factory = has(n, "factory", "tools only", "tools-only", "full-flash", "full flash",
      "5 bins", "efuse", "flash encryption", "fe-dev");
  }
  const commercial = has(v, "shop", "commercial", "infortech") || has(n, "infortechshop", "comercial");
  const provider = !commercial && !factory && has(v, "client", "tower", "provider");
  const plaintext_migration =
    has(v, "plaintext-migration") || (has(n, "migration") && has(n, "plaintext"));
  // HARDEN-1 — migração de schema/FS: plaintext, otafs2c ou BENCH ONLY.
  const schema_migration =
    plaintext_migration || has(v, "otafs2c") ||
    has(n, "otafs2c", "bench only", "bench-only", "may reset", "reset de config");
  return {
    kind,
    factory_tools_only: factory,
    requires_fe: factory,
    commercial,
    provider,
    plaintext_migration,
    schema_migration,
    may_reset_config: schema_migration,
  };
}

export function deviceIsCommercial(deviceFw) {
  return classifyRelease(deviceFw).commercial;
}

export function schemaResetRisk(deviceFw, releaseVersion, cls) {
  if (cls && cls.schema_migration) return true;
  const dv = parseVersion(deviceFw);
  const tv = parseVersion(releaseVersion);
  if (dv === null) return false;
  if (cmp(dv, SCHEMA_STABLE_MIN) < 0 && (tv === null || cmp(tv, SCHEMA_STABLE_MIN) >= 0)) {
    return true;
  }
  return false;
}

/** HARDEN-1 — app OTA perigoso? (migração + device não-pronto: fw<2.8.10 OU sem fs). */
export function dangerousAppOta(deviceFw, releaseVersion, cls, fsSupported) {
  if (!cls || !cls.schema_migration) return false;
  const dv = parseVersion(deviceFw);
  const tv = parseVersion(releaseVersion);
  const targetNew = tv === null || cmp(tv, SCHEMA_STABLE_MIN) >= 0;
  const deviceReady = dv !== null && cmp(dv, SCHEMA_STABLE_MIN) >= 0 && !!fsSupported;
  return targetNew && !deviceReady;
}

/** HARDEN-1 — release pode ir por canal de massa (bulk/rollout)? Espelha o backend. */
export function releaseBulkEligible(version, notes = "", kind = "app") {
  const cls = classifyRelease(version, notes, kind);
  if (cls.factory_tools_only) return { ok: false, code: "blocked_factory_tools_only" };
  if (cls.schema_migration) return { ok: false, code: "blocked_schema_migration_release" };
  if (cls.commercial) return { ok: false, code: "blocked_commercial_release" };
  return { ok: true, code: "ok" };
}

const HARD_BLOCK_MSG =
  "Atualização bloqueada: este salto pode resetar Wi-Fi/NVS, deixar o equipamento em AP e " +
  "quebrar a página local (incidente IPWD-D4EB9A88). Use flash completo em bancada (USB) ou " +
  "um firmware migrador específico.";

/**
 * @returns {{decision:'allow'|'block'|'needs_reset_confirm', code, message, classification, deviceFw, target, overrideUsed?}}
 */
export function evaluateOtaCompat(deviceFw, release, {
  confirmConfigReset = false, fsSupported = false, isProtected = false,
  isPlatformAdmin = false, dangerousAck = false,
} = {}) {
  const version = release?.version;
  const notes = release?.notes;
  const kind = release?.kind || "app";
  const cls = classifyRelease(version, notes, kind);
  const base = { classification: cls, deviceFw: deviceFw || null, target: version || null };

  if (cls.factory_tools_only) {
    return {
      ...base, decision: "block", code: "blocked_factory_tools_only",
      message:
        "Release factory/Tools-only (FE-dev): precisa de full-flash dos 5 bins pelo Tools " +
        "(com Flash Encryption no 1º boot). NÃO é OTA app — brica um device plaintext.",
    };
  }
  // Salto PERIGOSO de migração de schema/FS (ex.: 2.8.9 → 2.8.12-otafs2c-migration).
  if (dangerousAppOta(deviceFw, version, cls, fsSupported)) {
    if (isProtected) {
      return { ...base, decision: "block", code: "blocked_protected_dangerous",
        message: "Device PROTEGIDO: OTA app de migração é bloqueado sem exceção. " + HARD_BLOCK_MSG };
    }
    if (dangerousAck && isPlatformAdmin) {
      return { ...base, decision: "allow", code: "dangerous_override_ack", overrideUsed: true,
        message: "Override de bancada (super_admin ciente do risco de reset)." };
    }
    return { ...base, decision: "block", code: "blocked_requires_nvs_migrator_or_fs_companion",
      message: HARD_BLOCK_MSG };
  }
  // Release comercial em device não-comercial.
  if (cls.commercial && !deviceIsCommercial(deviceFw)) {
    if (isProtected) {
      return { ...base, decision: "block", code: "blocked_protected_commercial",
        message: "Device protegido: release comercial bloqueada sem exceção." };
    }
    if (dangerousAck && isPlatformAdmin) {
      return { ...base, decision: "allow", code: "commercial_override_ack", overrideUsed: true,
        message: "Override de bancada (release comercial em device não-comercial)." };
    }
    return { ...base, decision: "block", code: "blocked_commercial_profile_mismatch",
      message: "Release COMERCIAL (loja/InfortechShop: AP aberto + admin/admin) num device que NÃO é comercial." };
  }
  // Risco LEVE (cruza 2.8.10 sem ser release de migração) → confirmar com a frase.
  if (!cls.schema_migration && schemaResetRisk(deviceFw, version, cls) && !confirmConfigReset) {
    return {
      ...base, decision: "needs_reset_confirm", code: "config_reset_risk",
      message:
        `O salto ${deviceFw || "?"} → ${version || "?"} pode resetar a configuração por evolução ` +
        "de schema NVS. Planeje reconfiguração pós-OTA.",
    };
  }
  return { ...base, decision: "allow", code: "ok", message: "compatível" };
}

/** Rótulo/tom p/ badge do tipo de release (usado na lista/painel). */
export function releaseTypeBadge(release) {
  const c = classifyRelease(release?.version, release?.notes, release?.kind);
  if (c.factory_tools_only) return { label: "FACTORY/FE", tone: "err", title: "FE-dev/factory — full-flash pelo Tools" };
  if (c.commercial) return { label: "COMERCIAL", tone: "warn", title: "SKU comercial (loja)" };
  if (c.schema_migration) return { label: "MIGRAÇÃO", tone: "warn", title: "Migração de schema/FS (pode resetar config)" };
  if (c.provider) return { label: "PROVEDOR", tone: "muted", title: "Provedor (cliente/torre)" };
  return { label: "APP", tone: "muted", title: "Firmware app" };
}
